import { getNoticeInfo } from '@/api/dashboard'
import { registerTask, removeTask } from '@/services/timerService'
import { useModalManagerStore } from '@/store/modalManager'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const VERIFY_TASK_ID = 'verify-tip-task'
const DEFAULT_INTERVAL = 60

const useVerifyStore = defineStore('verify', () => {
  const modalStore = useModalManagerStore()
  const router = useRouter()
  const showTip = ref(false)
  const tipTime = ref(0)
  const loading = ref(false)
  const initialized = ref(false)
  const disabled = ref(false)
  const modalOpening = ref(false)

  const clearScheduledFetch = () => {
    removeTask(VERIFY_TASK_ID)
  }

  const scheduleNextFetch = (seconds?: number) => {
    clearScheduledFetch()
    if (disabled.value) return

    const explicit = Number(seconds)
    const source = Number.isFinite(explicit) && explicit > 0 ? explicit : tipTime.value
    const interval = source > 0 ? source : DEFAULT_INTERVAL

    registerTask(VERIFY_TASK_ID, dayjs().add(interval, 'second'), () => {
      void fetchNoticeInfo(true)
    })
  }

  const openVerifyTipsModal = async () => {
    if (modalOpening.value) return
    modalOpening.value = true
    try {
      const response = await modalStore.open(
        'VerifyTipsModal',
        {},
        { priority: 'high', resolveOnCancel: true },
      )
      if (response.ok) {
        const modalId = (response.data as any)?.modalId
        if (modalId) {
          modalStore.operationSuccess(modalId, { success: true })
        }
        await router.push({
          name: '(public).settings.security-center',
          query: {
            tab: 'security-center',
          },
        })
      }
    } catch (error) {
      console.error('[verifyStore] open VerifyTipsModal failed:', error)
    } finally {
      modalOpening.value = false
    }
  }

  const fetchNoticeInfo = async (fromTimer = false) => {
    if (disabled.value) return
    if (!fromTimer) loading.value = true

    try {
      const res = await getNoticeInfo()
      const nextInterval = Number(res.tip_time) || 0
      tipTime.value = nextInterval
      showTip.value = !!res.show_tip
      disabled.value = res.show_tip === false && nextInterval <= 0

      if (res.show_tip) {
        await openVerifyTipsModal()
      }

      scheduleNextFetch(nextInterval)
    } catch (error) {
      console.error('[verifyStore] fetchNoticeInfo failed:', error)
      scheduleNextFetch(DEFAULT_INTERVAL)
    } finally {
      if (!fromTimer) loading.value = false
      if (!initialized.value) initialized.value = true
    }
  }

  const initVerifyInfo = async () => {
    if (initialized.value) return
    await fetchNoticeInfo(false)
  }

  return {
    showTip,
    tipTime,
    loading,
    disabled,
    fetchNoticeInfo,
    initVerifyInfo,
    scheduleNextFetch,
    clearScheduledFetch,
  }
})

export { useVerifyStore }
export default useVerifyStore
